import {
	breakpointsTailwind,
	useBreakpoints,
	useWindowScroll,
	watchThrottled
} from '@vueuse/core';

const SHORTS_PATH = '/shorts';
/** Entra no modo compacto ao rolar para baixo além deste valor (desktop). */
const SCROLL_COMPACT_ENTER_DESKTOP = 64;
/** Mobile: limite maior para não esconder a barra com scroll pequeno. */
const SCROLL_COMPACT_ENTER_MOBILE = 120;
/** No topo da página, sempre expandido. */
const SCROLL_EXIT_TOP = 8;
/** Abaixo deste valor considera “no topo” e exibe o título “Shorts”. */
const SCROLL_AT_TOP_FOR_TITLE = 24;
/** Após trocar compacto ↔ expandido o layout muda e o scrollY “pula”; ignorar leituras instáveis. */
const LAYOUT_SETTLE_MS_DESKTOP = 280;
const LAYOUT_SETTLE_MS_MOBILE = 500;
const LAYOUT_SETTLE_MS_DESKTOP_AFTER_SCROLL_UP = 440;
const LAYOUT_SETTLE_MS_MOBILE_AFTER_SCROLL_UP = 520;
/**
 * Depois de expandir, reflow/sticky pode aumentar `scrollY` por vários frames; sem cooldown isso
 * reativa o modo compacto logo após mostrar o título.
 */
const SCROLL_DOWN_COMPACT_COOLDOWN_MS_DESKTOP = 900;
const SCROLL_DOWN_COMPACT_COOLDOWN_MS_MOBILE = 1000;
/** Durante o settle, só forçar expand se estiver no topo. */
const FORCE_EXPAND_Y = 6;

/**
 * Estado compartilhado (layout + barra Shorts): scroll para baixo compacta; para cima expande
 * sem precisar chegar no topo. Só no cliente (após guard SSR).
 */
export function useShortsScrollCompact() {
	const route = useRoute();
	const isCompact = useState('shorts-scroll-compact', () => false);
	const isAtTop = useState('shorts-scroll-at-top', () => true);

	if (import.meta.server) {
		return { isCompact, isAtTop };
	}

	const isDesktop = useBreakpoints(breakpointsTailwind).greater('lg');
	const { y } = useWindowScroll();

	let layoutSettleUntil = 0;
	let lastY = 0;
	let pendingScrollBaselineReset = false;
	let scrollDownCompactBlockedUntil = 0;

	function expandAfterScroll(now: number): void {
		isCompact.value = false;
		layoutSettleUntil =
			now +
			(isDesktop.value
				? LAYOUT_SETTLE_MS_DESKTOP_AFTER_SCROLL_UP
				: LAYOUT_SETTLE_MS_MOBILE_AFTER_SCROLL_UP);
		pendingScrollBaselineReset = true;
		const cooldownMs = isDesktop.value
			? SCROLL_DOWN_COMPACT_COOLDOWN_MS_DESKTOP
			: SCROLL_DOWN_COMPACT_COOLDOWN_MS_MOBILE;
		scrollDownCompactBlockedUntil = now + cooldownMs;
	}

	function compactAfterScrollDown(now: number): void {
		isCompact.value = true;
		layoutSettleUntil =
			now +
			(isDesktop.value ? LAYOUT_SETTLE_MS_DESKTOP : LAYOUT_SETTLE_MS_MOBILE);
	}

	function syncFromScroll() {
		if (!route.path.startsWith(SHORTS_PATH)) {
			isCompact.value = false;
			isAtTop.value = true;
			layoutSettleUntil = 0;
			pendingScrollBaselineReset = false;
			scrollDownCompactBlockedUntil = 0;
			lastY = y.value;
			return;
		}

		const now = Date.now();
		const yStable = Math.max(0, Math.floor(y.value));
		const scrollingUp = yStable < lastY;
		const scrollingDown = yStable > lastY;

		isAtTop.value = yStable <= SCROLL_AT_TOP_FOR_TITLE;

		if (now < layoutSettleUntil) {
			if (isCompact.value && yStable <= FORCE_EXPAND_Y) {
				expandAfterScroll(now);
			}
			if (!isCompact.value) {
				return;
			}
			lastY = yStable;
			return;
		}

		if (pendingScrollBaselineReset && !isCompact.value) {
			lastY = yStable;
			pendingScrollBaselineReset = false;
		}

		if (yStable <= SCROLL_EXIT_TOP) {
			if (isCompact.value) {
				expandAfterScroll(now);
			}
			lastY = yStable;
			return;
		}

		if (scrollingUp && isCompact.value) {
			expandAfterScroll(now);
			lastY = yStable;
			return;
		}

		const compactThreshold = isDesktop.value
			? SCROLL_COMPACT_ENTER_DESKTOP
			: SCROLL_COMPACT_ENTER_MOBILE;

		const scrollDownAllowsCompact =
			scrollingDown &&
			yStable >= compactThreshold &&
			!isCompact.value &&
			now >= scrollDownCompactBlockedUntil;

		if (scrollDownAllowsCompact) {
			compactAfterScrollDown(now);
		}

		lastY = yStable;
	}

	watchThrottled([y, () => route.path], syncFromScroll, { throttle: 50 });

	watch(
		() => route.path,
		path => {
			layoutSettleUntil = 0;
			scrollDownCompactBlockedUntil = 0;
			if (!path.startsWith(SHORTS_PATH)) {
				isCompact.value = false;
			} else {
				syncFromScroll();
			}
		},
		{ immediate: true }
	);

	onMounted(() => {
		layoutSettleUntil = 0;
		syncFromScroll();
	});

	return { isCompact, isAtTop };
}
